// apps/web/src/components/ui/Alert.tsx
//
// Top-level error / info banners. Distinct from FormField's per-field
// error text. Used for "the whole call failed" surfaces.

import type { ReactNode } from 'react';

export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

export interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  requestId?: string | null;
  children: ReactNode;
  className?: string;
}

const variantClasses: Record<AlertVariant, string> = {
  error: 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  info: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200',
  success: 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200',
};

export function Alert({ variant = 'info', title, requestId, children, className }: AlertProps): JSX.Element {
  const cls = [
    'rounded border p-3 text-sm',
    variantClasses[variant],
    className ?? '',
  ].join(' ');
  return (
    <div role={variant === 'error' ? 'alert' : 'status'} className={cls}>
      {title ? <p className="font-medium">{title}</p> : null}
      <div className="mt-1">{children}</div>
      {requestId ? (
        <p className="mt-2 text-xs opacity-75">
          requestId: <span className="font-mono">{requestId}</span>
        </p>
      ) : null}
    </div>
  );
}
